/** @jsxImportSource @emotion/react */
import contoursThumbnail from '@app/assets/images/contours-basemap-thumbnail.png'
import satelliteThumbnail from '@app/assets/images/satellite-basemap-thumbnail.png'
import streetThumbnail from '@app/assets/images/street-basemap-thumbnail.png'
import { useTheme } from '@mui/material/styles'
import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useState } from 'react'
import { Icon } from '@app/components/Atoms/Icon'
import { handleChangeMapStyle } from '@app/contexts/ModuleMapContexts/utils/style'
import { usePreferences } from '@app/hooks/usePreferences'
import {
  riskBasemapAtom,
  riskMapInstanceAtom,
  riskMapInteractiveAtom,
  riskMapStyleAtom,
} from '@app/risk-migration/atoms/riskMap.atoms'
import { Box, Typography } from '@mui/material'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  baseMapButton,
  baseMapButtonContainer,
  baseMapTitleWrapper,
  previewContainer,
  previewMap,
  previewMapContainer,
} from './BaseMapButton.styles'

export const BaseMapButton = () => {
  const theme = useTheme()
  const [showPreview, setShowPreview] = useState(false)
  const { basemap_list } = usePreferences()
  const setStyle = useSetAtom(riskMapStyleAtom)
  const changeMapStyle = useCallback(
    ({ style }: { style: string }) => {
      handleChangeMapStyle({ style, setStyle })
    },
    [setStyle],
  )
  const map = useAtomValue(riskMapInstanceAtom)
  const interactive = useAtomValue(riskMapInteractiveAtom)
  const [basemap, setBasemap] = useAtom(riskBasemapAtom)

  // Derived from `basemap`, not tracked separately — a desyncing index was the bug.
  const currentIndex = basemap_list.findIndex((bMap) => bMap.label === basemap)
  const safeCurrentIndex = currentIndex === -1 ? 0 : currentIndex

  const getThumbnail = (type: string) => {
    switch (type) {
      case 'satellite':
        return satelliteThumbnail
      case 'contours':
        return contoursThumbnail
      case 'street':
      default:
        return streetThumbnail
    }
  }

  const cycleBaseMap = useCallback(() => {
    const index = basemap_list.findIndex((bMap) => bMap.label === basemap)
    const nextIndex = index < basemap_list.length - 1 ? index + 1 : 0
    setBasemap(basemap_list[nextIndex].label)
  }, [basemap_list, basemap, setBasemap])

  // Reset the base map style if the basemap list changes
  // Try to match up, if not, default to the first one
  useEffect(() => {
    if (!map || !basemap_list || basemap_list.length === 0) return
    const mapStyleSprite = map.getStyle()?.sprite
    if (!mapStyleSprite) return
    // Extract the value after 'urbanintelligence' or 'mapbox' up to either a forward slash or a '?'
    const spriteMatch = mapStyleSprite.match(/(?:urbanintelligence|mapbox)\/([^/?]*)/)
    const sourceMatchIndex = basemap_list.findIndex((bMap) => {
      const match = bMap.source.match(/(?:urbanintelligence|mapbox)\/([^/?]*)/)
      return match && spriteMatch && match[1] === spriteMatch[1]
    })
    if (spriteMatch && sourceMatchIndex !== -1) return
    // const indexNum = sourceMatchIndex !== -1 ? sourceMatchIndex : 0
    // try to find similar style using basemap label
    const indexNum = basemap_list.findIndex((bMap) => bMap.label === basemap) || 0

    // if a flyto is in progress, wait for it to finish before changing the style
    // otherwise the map changes and the flyto is cancelled
    // assume the map is flying already
    map.once('moveend', () => {
      setTimeout(() => {
        changeMapStyle({ style: basemap_list[indexNum].source })
        setShowPreview(false)
      }, 0)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basemap_list, basemap])

  // Applies the style whenever basemap changes — both cycle and the picker go through setBasemap.
  useEffect(() => {
    const index = basemap_list.findIndex((bMap) => bMap.label === basemap)
    if (index !== -1) {
      changeMapStyle({ style: basemap_list[index].source })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basemap])

  if (!basemap_list || basemap_list.length === 0) {
    return null
  }

  return interactive ? (
    <Box
      css={baseMapButtonContainer({ theme })}
      onMouseEnter={() => setShowPreview(true)}
      onMouseLeave={() => setShowPreview(false)}
      className="tutorial-basemap"
    >
      <Box
        component="button"
        onClick={cycleBaseMap}
        css={baseMapButton({
          thumbnail: getThumbnail(basemap_list[safeCurrentIndex].type),
          theme,
        })}
      >
        <Box css={baseMapTitleWrapper({ theme })}>
          <Icon iconName="Layers" size="medium" colour={theme.palette.white.main} />
          <Typography variant="body1" color={theme.palette.white.main}>
            View
          </Typography>
        </Box>
      </Box>
      <AnimatePresence>
        {showPreview && (
          <motion.div
            css={previewContainer({ theme })}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {basemap_list.map((bMap, index) => {
              const isSelected = index === safeCurrentIndex

              return (
                <Box
                  key={bMap.label}
                  css={previewMapContainer({ theme })}
                  onClick={() => setBasemap(bMap.label)}
                >
                  <Box
                    component="span"
                    css={previewMap({ theme, isSelected, thumbnail: getThumbnail(bMap.type) })}
                  />
                  <Typography variant="body2">{bMap.label}</Typography>
                </Box>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  ) : null
}
